import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';

import { ClientRoutes } from '@klp/client/config';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Heading } from '@klp/design';
import { Amount } from '@klp/design/Amount';
import { Loader } from '@klp/design/Loader';
import type { ITableConsumerState } from '@klp/design/Table';
import { Table, TableColumnDef } from '@klp/design/Table';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { ErrorNotification } from '@klp/errorNotification';
import { getDefaultLanguage } from '@klp/layout/Sidebar/config';

import { getDownloadContractEnable } from '../config';
import { useGetContractLeaseAgreementQuery } from '../rtkApi';
import type { LeaseAgreement } from '../types';

import { ContractDownload } from './ContractDownload';

const styles = (theme: Theme) => ({
    download: {
        textAlign: 'center',
        whiteSpace: 'nowrap',
        '& > *': {
            display: 'inline-block',
            '& > *': {
                margin: theme.spacing(1),
            },
        },
    },
    previewDownload: {
        cursor: 'pointer',
    },
    fileDisplay: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        textAlign: 'center' as const,
        maxWidth: '120px',
    },
    fileList: {
        display: 'flex',
        gap: theme.spacing(2),
    },
});

export const LeaseAgreementTable: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const params = useAppParams();
    const { selectedClient } = params;

    const kundeNummer = selectedClient?.kundeNummer;

    const {
        data: contractLeaseAgreementData,
        isLoading: contractLeaseAgreementLoading,
        isError: contractLeaseAgreementError,
    } = useGetContractLeaseAgreementQuery(
        { kundeNummer: kundeNummer! },
        { skip: !kundeNummer },
    );

    const contractLeaseAgreement =
        contractLeaseAgreementData?.contractLeaseAgreement;

    const defaultLanguage = getDefaultLanguage();
    const danskDeployment = defaultLanguage === 'da';
    const svenskDeployment = defaultLanguage === 'sv';
    const danskOrSvenskDeployment = danskDeployment || svenskDeployment;

    const renderContractNumber = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: LeaseAgreement,
    ): JSX.Element => (
        <>
            {!selectedClient && (
                <>
                    {row.kundeNavn}
                    <br />
                </>
            )}

            {row.kontraktNummer}
            <br />
            {!danskOrSvenskDeployment && row.contractLineName}
        </>
    );

    const renderContractName = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: LeaseAgreement,
    ): JSX.Element => (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
            {danskOrSvenskDeployment
                ? row.contractLineName
                : row.rentalobjectName}
        </>
    );

    const renderArealAndQuantity = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: LeaseAgreement,
    ) => {
        const { rentalQty, areal, arealUnits } = row;

        if (areal !== '-' && rentalQty > 0) {
            return (
                <>
                    {rentalQty} {t('kontrakt.pcs')} ({`${areal} ${arealUnits}`})
                </>
            );
        } else if (areal !== '-' && rentalQty === 0) {
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <>{`${areal}${arealUnits}`}</>;
        } else if (areal === '-' && rentalQty > 0) {
            return (
                <>
                    {rentalQty} {t('kontrakt.pcs')}
                </>
            );
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    };

    const renderPricePerPlaceAndM2 = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: LeaseAgreement,
    ) => {
        const { pricePerPlace, pricePerM2, lokalValuta } = row;

        if (typeof pricePerPlace === 'number' && pricePerPlace > 0) {
            return (
                <>
                    <Amount value={pricePerPlace} /> &#8239;{lokalValuta}
                    {t('kontrakt.perStk')}
                </>
            );
        }

        if (typeof pricePerM2 === 'number' && pricePerM2 > 0) {
            return (
                <>
                    <Amount value={pricePerM2} /> &#8239;{lokalValuta}
                    {t('kontrakt.perM2')}
                </>
            );
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    };

    const renderPricePerYear = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: LeaseAgreement,
    ): JSX.Element => {
        const { lokalValuta } = row;
        return (
            <>
                <Amount value={_value} /> &#8239;{lokalValuta}
                {t('kontrakt.perAar')}
            </>
        );
    };

    const tableDef: Array<TableColumnDef<LeaseAgreement, unknown>> = [
        new TableColumnDef<LeaseAgreement, unknown>({
            key: 'kontraktNummer',
            translation: 'leaseAgreement.table.contractNumber',
            align: 'left',
            type: 'number',
            sortable: true,
            export: true,
            render: renderContractNumber,
        }),
        new TableColumnDef<LeaseAgreement, unknown>({
            key: 'rentalobjectName',
            translation: 'leaseAgreement.table.name',
            align: 'left',
            sortable: true,
            export: true,
            render: renderContractName,
        }),
        new TableColumnDef<LeaseAgreement, unknown>({
            key: 'validFrom',
            translation: 'leaseAgreement.table.leaseFromDate',
            align: 'left',
            type: 'date',
            sortable: true,
            export: true,
        }),
        ...(!danskDeployment
            ? [
                  new TableColumnDef<LeaseAgreement, unknown>({
                      key: 'validTo',
                      translation: 'leaseAgreement.table.leaseToDate',
                      align: 'left',
                      type: 'date',
                      sortable: true,
                      export: true,
                  }),
              ]
            : []),
        ...(svenskDeployment
            ? [
                  new TableColumnDef<LeaseAgreement, unknown>({
                      key: 'fristOppsigelseLeietaker',
                      translation: 'kontrakt.fristOppsigelseLeietaker',
                      align: 'left',
                      type: 'date',
                      sortable: true,
                      export: true,
                  }),
              ]
            : []),
        new TableColumnDef({
            key: '',
            translation: 'kontrakt.antall',
            align: 'right',
            render: renderArealAndQuantity,
        }),
        new TableColumnDef({
            key: 'pricePerM2',
            translation: 'kontrakt.pricePerUnit',
            sortable: true,
            type: 'number',
            render: renderPricePerPlaceAndM2,
        }),
        new TableColumnDef({
            key: 'amount',
            translation: 'kontrakt.prisPerAar',
            align: 'right',
            sortable: true,
            type: 'number',
            render: renderPricePerYear,
        }),
        new TableColumnDef<LeaseAgreement, unknown>({
            key: 'byggNummer',
            translation: 'leaseAgreement.table.propertyNumber',
            align: 'left',
            sortable: true,
            export: true,
        }),
        new TableColumnDef<LeaseAgreement, unknown>({
            key: 'byggNavn',
            translation: 'leaseAgreement.table.propertyName',
            align: 'left',
            sortable: true,
            export: true,
        }),
    ];

    const renderContractDownload = <DataType, FilterType>(
        _value: unknown,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: LeaseAgreement,
    ): JSX.Element => {
        return ContractDownload(row, classes);
    };

    if (getDownloadContractEnable()) {
        tableDef.push(
            new TableColumnDef<LeaseAgreement, unknown>({
                translation: 'kontrakt.relatedFiles',
                key: 'dokuments',
                render: renderContractDownload,
            }),
        );
    }

    const filteredLeaseAgreements = danskOrSvenskDeployment
        ? contractLeaseAgreement?.leaseagreementList.filter(
              (l) => !(l.arealIkkeMedregnet && l.amount === 0),
          )
        : contractLeaseAgreement?.leaseagreementList;

    const filterByStatus = (status: string): Array<LeaseAgreement> => {
        return (
            filteredLeaseAgreements?.filter((l) => l.status === status) || []
        );
    };

    const hasStatus = (status: string): boolean => {
        return (
            filteredLeaseAgreements?.some((l) => l.status === status) || false
        );
    };

    const shouldShowActiveAgreements =
        !filteredLeaseAgreements ||
        filteredLeaseAgreements.length === 0 ||
        hasStatus('Active');

    return (
        <Loader isLoading={contractLeaseAgreementLoading}>
            <Conditional
                condition={!!selectedClient}
                errorComponent={
                    <ErrorNotification
                        title="clientSelect.errorNoClientsSelected.title"
                        description="clientSelect.errorNoClientsSelected.description"
                        backTarget={AppRoutes.mainPage()}
                        backTitle="general.goBackToFrontpage"
                        mainTarget={ClientRoutes.clientSelect()}
                        mainTitle="clientSelect.chooseClient"
                        returnUrl={true}
                    />
                }
            >
                <Conditional
                    condition={!contractLeaseAgreementError}
                    errorComponent={
                        <ErrorNotification
                            title="leaseAgreement.errorDownload.title"
                            description="leaseAgreement.errorDownload.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                        />
                    }
                >
                    {shouldShowActiveAgreements && (
                        <Block marginVertical="fat">
                            <Heading
                                klpHeadingLevel="FOUR"
                                translation="leaseAgreement.activeAgreement"
                            />
                            <Table
                                id="LeaseAgreementTable"
                                emptyTableMessage="leaseAgreement.table.notFound"
                                data={filterByStatus('Active')}
                                columns={tableDef}
                                initialRowsPerPage={10}
                                sortBy={tableDef[2]}
                                sortByDirection="desc"
                            />
                        </Block>
                    )}
                    {hasStatus('Future') && (
                        <Block marginVertical="fat">
                            <Heading
                                klpHeadingLevel="FOUR"
                                translation="leaseAgreement.futureAgreement"
                            />
                            <Table
                                id="FutureLeaseAgreementTable"
                                emptyTableMessage="leaseAgreement.table.notFound"
                                data={filterByStatus('Future')}
                                columns={tableDef}
                                initialRowsPerPage={10}
                                sortBy={tableDef[2]}
                                sortByDirection="desc"
                            />
                        </Block>
                    )}
                    {hasStatus('Archived') && (
                        <Block marginVertical="fat">
                            <Heading
                                klpHeadingLevel="FOUR"
                                translation="leaseAgreement.archivedAgreement"
                            />
                            <Table
                                id="ArchivedLeaseAgreementTable"
                                emptyTableMessage="leaseAgreement.table.notFound"
                                data={filterByStatus('Archived')}
                                columns={tableDef}
                                initialRowsPerPage={10}
                                sortBy={tableDef[2]}
                                sortByDirection="desc"
                            />
                        </Block>
                    )}
                </Conditional>
            </Conditional>
        </Loader>
    );
};
